// API functions for billing context management
// Stage 15: Billing Alternatives & Context Switching

import { client as apiClient } from "./api/client.gen";
import type { BillingContext, BillingAlternative } from "../types/billing";

interface ApiEnvelope<T> {
  data: T;
  correlationId: string;
  eventId: string;
  error: null | { code: string; message: string; breadcrumb: string };
  meta?: Record<string, unknown>;
}

function unwrap<T>(
  res: { data?: ApiEnvelope<T> | null },
  fallbackMsg: string,
): T {
  if (!res.data || res.data.error) {
    throw new Error(res.data?.error?.message ?? fallbackMsg);
  }
  return res.data.data;
}

// ============================================================================
// Billing Contexts
// ============================================================================

interface BillingContextsResponse {
  contexts: BillingContext[];
  userDefault: {
    defaultOrganisationId: string | null;
  };
}

export async function getBillingContexts(): Promise<BillingContextsResponse> {
  const res = await apiClient.get<ApiEnvelope<BillingContextsResponse>>({
    url: "/v1/billing/contexts",
  });
  return unwrap(res, "Failed to fetch billing contexts");
}

export async function setBillingDefault(
  defaultOrganisationId: string | null,
): Promise<{ defaultOrganisationId: string | null }> {
  const res = await apiClient.patch<
    ApiEnvelope<{ defaultOrganisationId: string | null }>
  >({
    url: "/v1/users/me/billing-default",
    body: { defaultOrganisationId } as unknown as undefined,
  });
  return unwrap(res, "Failed to set billing default");
}

// ============================================================================
// Project Billing Preferences
// ============================================================================

interface ProjectBillingPreference {
  projectId: string;
  preferenceType: string;
  organisationId: string | null;
  organisationName: string | null;
}

export async function getProjectBillingPreference(
  projectId: string,
): Promise<ProjectBillingPreference> {
  const res = await apiClient.get<ApiEnvelope<ProjectBillingPreference>>({
    url: `/v1/projects/${projectId}/billing-preference`,
  });
  return unwrap(res, "Failed to fetch project billing preference");
}

export async function setProjectBillingPreference(
  projectId: string,
  preferenceType: string,
  organisationId?: string | null,
): Promise<ProjectBillingPreference> {
  const res = await apiClient.patch<ApiEnvelope<ProjectBillingPreference>>({
    url: `/v1/projects/${projectId}/billing-preference`,
    body: {
      preferenceType,
      organisationId: organisationId ?? null,
    } as unknown as undefined,
  });
  return unwrap(res, "Failed to set project billing preference");
}

// ============================================================================
// My Subscription (M70 — resolve authenticated user's default subscription)
// ============================================================================

export interface MySubscription {
  subscriptionId: string;
  teamId: string;
  planId: number;
  statusId: number;
}

/**
 * Returns the authenticated user's default subscription, or null when the
 * user has no team with an open subscription. The team_id from this call
 * is the input to {@link listTeamInvoices}.
 */
export async function getMySubscription(): Promise<MySubscription | null> {
  const res = await apiClient.get<ApiEnvelope<MySubscription | null>>({
    url: "/v1/users/me/subscription",
  });
  if (!res.data || res.data.error) {
    throw new Error(res.data?.error?.message ?? "Failed to fetch subscription");
  }
  return res.data.data;
}

// ============================================================================
// Credit Balance (live balance pill — GET /v1/billing/credits/balance)
// ============================================================================

export interface CreditBalance {
  /**
   * Spendable credits. SIGNED — may be NEGATIVE when a reversal (chargeback /
   * refund) clawed back more than the remaining balance, leaving the wallet in
   * debt (see the fraud/dispute epic §4.3). The header pill renders an "owed"
   * state when this is < 0.
   */
  available: number;
  reserved: number;
  total: number;
  /**
   * Credits held in a reversible frozen state (grants in `held` status — e.g. a
   * payment under review). Excluded from `available`; surfaced as a lock chip on
   * the header pill. 0 when nothing is frozen.
   */
  frozen: number;
  subscriptionId: string;
}

/**
 * A data-driven account restriction, surfaced via the status-reason pattern
 * (plan §5.1b). The frontend does NOT hard-code restriction types: it picks the
 * banner variant from `severity` and the message from `userFacingTk` + `params`.
 * A neutral, non-leaking i18n key is always supplied by the backend (a purely
 * internal reason is mapped to a neutral key server-side).
 */
export interface AccountRestriction {
  /** Machine slug (diagnostics/analytics only — never rendered to the user). */
  reasonSlug: string;
  /** Drives the banner colour/variant. */
  severity: "info" | "warning" | "critical" | "good";
  /** NEUTRAL i18n key for the user-facing message (e.g. account.reason.underReview). */
  userFacingTk: string;
  /** Interpolation values for the message copy. Any field may be absent. */
  params?: {
    frozenCredits?: number;
    availableCredits?: number;
    outstandingCredits?: number;
  };
}

/**
 * Returns the authenticated user's personal credit balance. The backend
 * self-resolves the caller's active subscription when no id is supplied.
 */
export async function getCreditBalance(): Promise<CreditBalance> {
  const res = await apiClient.get<ApiEnvelope<CreditBalance>>({
    url: "/v1/billing/credits/balance",
  });
  return unwrap(res, "Failed to fetch credit balance");
}

// ============================================================================
// Billable Accounts (header account switcher — the CHARGING axis)
//
// Each account is a subscription the caller can charge to. The `balance`
// mirrors the personal credit balance shape (available / reserved / total),
// scoped to that account's subscription. `isDefault` marks the account charges
// currently route to. Selecting a row sets the active billing account via
// setActiveBillingAccount; membership is validated server-side.
// ============================================================================

export interface BillableAccount {
  subscriptionId: string;
  teamId: string;
  organisationId: string | null;
  label: string;
  planSlug: string;
  balance: CreditBalance;
  isDefault: boolean;
  /**
   * Open, user-visible restrictions on the account (may be empty). Data-driven —
   * the UI renders each element by severity + userFacingTk without knowing the
   * restriction type. See {@link AccountRestriction}.
   */
  restrictions: AccountRestriction[];
}

/**
 * Returns the billable accounts (subscriptions) the authenticated user can
 * charge to, each with its own live balance. The account with `isDefault`
 * is where charges currently route.
 */
export async function getBillableAccounts(): Promise<BillableAccount[]> {
  const res = await apiClient.get<ApiEnvelope<BillableAccount[]>>({
    url: "/v1/billing/accounts",
  });
  return unwrap(res, "Failed to fetch billable accounts");
}

/**
 * Sets the caller's active billing account (the subscription charges route to).
 * Passing `null` clears the explicit choice → server falls back to the default.
 * Membership is validated server-side.
 */
export async function setActiveBillingAccount(
  subscriptionId: string | null,
): Promise<void> {
  const res = await apiClient.put<ApiEnvelope<unknown>>({
    url: "/v1/users/me/active-billing-account",
    body: { subscriptionId } as unknown as undefined,
  });
  if (!res.data || res.data.error) {
    throw new Error(
      res.data?.error?.message ?? "Failed to set active billing account",
    );
  }
}

// ============================================================================
// Team Invoices (M70 — billing periods + per-period totals)
// ============================================================================

export interface TeamInvoice {
  periodId: string;
  subscriptionId: string;
  planId: number | null;
  periodStart: string; // ISO timestamp
  periodEnd: string;
  stripeInvoiceId: string | null;
  stripePeriodId: string | null;
  totalAmount: string; // numeric returned as string from PG
  lineCount: number;
  currencyId: number;
}

export interface TeamInvoicesResponse {
  invoices: TeamInvoice[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export async function listTeamInvoices(
  teamId: string,
  options: { limit?: number; offset?: number } = {},
): Promise<TeamInvoicesResponse> {
  const { limit = 20, offset = 0 } = options;
  const res = await apiClient.get<ApiEnvelope<TeamInvoicesResponse>>({
    url: `/v1/teams/${teamId}/invoices`,
    query: { limit, offset } as unknown as undefined,
  });
  return unwrap(res, "Failed to fetch team invoices");
}

// ============================================================================
// Payments (real card charges — top-ups + subscription payments)
//
// Backed by the hand-written api-alpha route:
//   GET /v1/billing/payments?subscriptionId=&limit=&offset=
//
// Each row is an ACTUAL Stripe charge (a top-up purchase or a subscription
// payment), account-scoped by subscription. Amounts are MINOR units returned
// as strings (parse with Number() at the display layer); `currencyId` is the
// ISO 4217 numeric code (826 = GBP). `statusSlug` is one of: paid, completed,
// payment_failed, refunded, partially_refunded, disputed, dispute_lost,
// dispute_won. `hostedInvoiceUrl` (subscription) / `receiptUrl` (top-up) are the
// Stripe download links — either may be null when not yet captured. Newest-first
// (ordered by the DB).
// ============================================================================

export interface Payment {
  paymentId: string;
  paymentType: "subscription" | "top_up";
  /** `_plan.plans.id` for a subscription payment; null for a top-up. */
  planId: number | null;
  /** Charged amount in MINOR units, as a numeric STRING (e.g. "1000" = £10). */
  chargedAmountMinor: string;
  /** `_credit.currencies.id` ISO 4217 numeric code (826 = GBP). */
  currencyId: number;
  /** Credits granted by this payment, as a decimal STRING. */
  grantedCredits: string;
  statusId: number;
  statusSlug: string;
  /** ISO timestamps; any of paid/failed may be null depending on status. */
  initiatedAt: string;
  paidAt: string | null;
  failedAt: string | null;
  stripePaymentIntentId: string | null;
  stripeInvoiceId: string | null;
  /** Stripe hosted-invoice URL (subscription charges); null when uncaptured. */
  hostedInvoiceUrl: string | null;
  /** Stripe receipt URL (top-up charges); null when uncaptured. */
  receiptUrl: string | null;
}

export interface PaymentsResponse {
  payments: Payment[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * Lists the actual card payments (top-ups + subscription charges) for a single
 * billing account, newest-first. Scope is the account's `subscriptionId`; the
 * DB authorises the caller against it.
 */
export async function listPayments(
  subscriptionId: string,
  options: { limit?: number; offset?: number } = {},
): Promise<PaymentsResponse> {
  const { limit = 20, offset = 0 } = options;
  const res = await apiClient.get<ApiEnvelope<PaymentsResponse>>({
    url: "/v1/billing/payments",
    query: { subscriptionId, limit, offset } as unknown as undefined,
  });
  return unwrap(res, "Failed to fetch payments");
}

// ============================================================================
// Subscription Period Usage (M70 — per-action-alias breakdown)
// ============================================================================

export interface PeriodUsageRow {
  actionAliasId: number;
  aliasSlug: string;
  modality: string;
  perfClass: string;
  callCount: number;
  totalAmount: string;
  currencyId: number;
  cacheHits: number;
  failoverCalls: number;
}

export interface PeriodUsageResponse {
  periodId: string;
  subscriptionId: string;
  periodStart: string | null;
  periodEnd: string | null;
  rows: PeriodUsageRow[];
  totals: {
    amount: string;
    callCount: number;
    currencyId: number;
  };
}

export async function getSubscriptionPeriodUsage(
  subscriptionId: string,
  periodId: string,
): Promise<PeriodUsageResponse> {
  const res = await apiClient.get<ApiEnvelope<PeriodUsageResponse>>({
    url: `/v1/subscriptions/${subscriptionId}/periods/${periodId}/usage`,
  });
  return unwrap(res, "Failed to fetch period usage");
}

// ============================================================================
// Subscription plans + management (Billing Subscription Management, part 2)
//
// Backed by the new api-alpha routes:
//   GET  /v1/billing/plans              → list purchasable plans + current
//   POST /v1/billing/change-subscription→ no-payment plan change (e.g. downgrade)
//   POST /v1/billing/checkout-session   → Stripe Checkout redirect URL
//   POST /v1/billing/portal-session     → Stripe Billing Portal redirect URL
//
// Wire fields are snake_case (the DB shape); we keep them verbatim so the UI
// maps them explicitly. `price_excl_vat_amount` is MINOR units (pence);
// currency 826 = GBP.
// ============================================================================

/** A single purchasable plan as returned by GET /v1/billing/plans. */
export interface PlanDto {
  id: number;
  slug: string;
  /** Translation key for the plan's display name. */
  tk: string;
  /** Translation key for the billing periodicity (e.g. monthly / yearly). */
  billing_periodicity_tk: string;
  /** Monthly credit allowance, decimal string; null = none. */
  credits?: string | null;
  /** Price in MINOR units (pence); null for free plans. */
  price_excl_vat_amount: number | null;
  /** `_credit.currencies.id` numeric code (826 = GBP). */
  price_currency_id: number;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  /** Stripe price lookup key — stable alias for the price, when configured. */
  stripe_price_lookup_key?: string | null;
  /** Whether this is the caller's current plan; null when unknown. */
  is_current: boolean | null;
}

/** The caller's current plan, with subscription-lifecycle detail. */
export interface CurrentPlanDto extends PlanDto {
  /** Whether the caller may subscribe / change to a different plan. */
  can_subscribe: boolean;
  /** Whether the subscription is in grace / past due (payment problem). */
  payment_past_due: boolean;
  /** Whether the plan has no positive price (free / perpetual). */
  is_free: boolean;
  subscription_id: string | null;
  /** The plan a scheduled downgrade will move to at period end, if any. */
  next_plan_id: number | null;
  /** ISO timestamp the current plan is cancelled/downgraded at, if scheduled. */
  cancel_at: string | null;
  /** ISO timestamp of the next payment, if any. */
  next_payment_at: string | null;
}

export interface AvailablePlansResponse {
  plans: PlanDto[];
  current: CurrentPlanDto | null;
}

/** Whether a plan change takes effect now or at the end of the period. */
export type ChangeSubscriptionEffect = "immediate" | "at_period_end";

/**
 * Lists the purchasable plans plus the caller's current plan/subscription.
 */
export async function getAvailablePlans(): Promise<AvailablePlansResponse> {
  const res = await apiClient.get<ApiEnvelope<AvailablePlansResponse>>({
    url: "/v1/billing/plans",
  });
  return unwrap(res, "Failed to fetch plans");
}

/**
 * Creates a Stripe Checkout session for the given plan. The caller redirects
 * the browser to the returned `url` to complete payment.
 */
export async function createCheckoutSession(
  planId: number,
): Promise<{ url: string }> {
  const res = await apiClient.post<ApiEnvelope<{ url: string }>>({
    url: "/v1/billing/checkout-session",
    body: { planId } as unknown as undefined,
  });
  return unwrap(res, "Failed to create checkout session");
}

/**
 * Opens the Stripe Billing Portal. The caller redirects the browser to the
 * returned `url` to manage payment methods, invoices, and cancellation.
 */
export async function openBillingPortal(): Promise<{ url: string }> {
  const res = await apiClient.post<ApiEnvelope<{ url: string }>>({
    url: "/v1/billing/portal-session",
  });
  return unwrap(res, "Failed to open billing portal");
}

/** Result of a no-payment subscription change. */
export interface ChangeSubscriptionResult {
  subscriptionId: string;
  planId: number;
  statusId: number;
  cancelAt?: string | null;
  nextPlanId?: number | null;
}

/**
 * Changes the caller's subscription without a payment step (the Downgrade-to-
 * Free path). `effective` defaults server-side; pass `"at_period_end"` to
 * schedule the change for the end of the current period.
 */
export async function changeSubscription(
  planId: number,
  effective?: ChangeSubscriptionEffect,
): Promise<ChangeSubscriptionResult> {
  const res = await apiClient.post<ApiEnvelope<ChangeSubscriptionResult>>({
    url: "/v1/billing/change-subscription",
    body: { planId, effective } as unknown as undefined,
  });
  return unwrap(res, "Failed to change subscription");
}

// ============================================================================
// Credit top-ups (one-time credit purchases)
//
// Backed by the api-alpha routes:
//   GET  /v1/billing/top-ups                 → list purchasable top-up packages
//   POST /v1/billing/top-ups/checkout-session→ Stripe Checkout redirect URL
//
// Wire fields are snake_case (the DB shape); we keep them verbatim so the UI
// maps them explicitly. Top-up prices use the SAME shape as plan prices:
// `price_excl_vat_amount` is in MINOR currency units (pence — e.g. 1000 = £10)
// and `price_currency_id` is the ISO 4217 numeric code (826 = GBP). `credits`
// is a decimal STRING (e.g. "100000") the UI formats with thousands separators.
// ============================================================================

/** A single purchasable credit top-up package as returned by GET /v1/billing/top-ups. */
export interface TopUpDto {
  id: number;
  slug: string;
  /** Translation key for the package's display name, when present. */
  tk?: string;
  /** Credit grant as a decimal STRING (e.g. "100000"). */
  credits: string;
  /** Price in MINOR units (pence — e.g. 1000 = £10); null when unpriced. */
  price_excl_vat_amount: number | null;
  /** `_credit.currencies.id` ISO 4217 numeric code (826 = GBP); null when unpriced. */
  price_currency_id: number | null;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  /** Stripe price lookup key — stable alias for the price, when configured. */
  stripe_price_lookup_key?: string | null;
}

export interface AvailableTopUpsResponse {
  topUps: TopUpDto[];
}

/**
 * Lists the purchasable one-time credit top-up packages.
 */
export async function getAvailableTopUps(): Promise<AvailableTopUpsResponse> {
  const res = await apiClient.get<ApiEnvelope<AvailableTopUpsResponse>>({
    url: "/v1/billing/top-ups",
  });
  return unwrap(res, "Failed to fetch top-ups");
}

/**
 * Creates a Stripe Checkout session for a top-up package charged to the given
 * subscription. The caller redirects the browser to the returned `url` to
 * complete payment.
 */
export async function createTopUpCheckoutSession(
  packagePlanId: number,
  subscriptionId: string,
): Promise<{ url: string }> {
  const res = await apiClient.post<ApiEnvelope<{ url: string }>>({
    url: "/v1/billing/top-ups/checkout-session",
    body: { packagePlanId, subscriptionId } as unknown as undefined,
  });
  return unwrap(res, "Failed to create top-up checkout session");
}

// ============================================================================
// Billing Alternatives
// ============================================================================

interface AlternativesResponse {
  alternatives: BillingAlternative[];
}

export async function resolveBillingAlternatives(
  currentContext: Record<string, unknown>,
  deniedDomains?: string[],
): Promise<AlternativesResponse> {
  const res = await apiClient.post<ApiEnvelope<AlternativesResponse>>({
    url: "/v1/billing/resolve-alternatives",
    body: { currentContext, deniedDomains } as unknown as undefined,
  });
  return unwrap(res, "Failed to resolve billing alternatives");
}

// ============================================================================
// Pre-run cost estimate (billable actions)
// ============================================================================
//
// Surfaces the per-action estimate computed by the DB
// (`_billing.estimate_action_costs` → `_billing.compute_price`) so the UI can
// show "this run will cost ~N" before the user commits.
//
// Backed by the user-facing `POST /v1/billing/estimate` route. The backend
// prices in CREDITS; the credit figure is in `estimated_credits` and the UI
// renders it via `formatCreditsExact`. Each item carries `cost_kind`
// ('known' = deterministic per-unit; 'estimated' = token/usage-based upper
// bound). See #237 for the environment='dev' pricing dependency.

/** One item in an estimate request — a single billable action to price. */
export interface ActionCostEstimateItem {
  /** Caller-supplied correlation ref echoed back on each result item. */
  clientRef: string;
  /**
   * Workflow the node belongs to. Required for workflow-node estimates: the
   * backend authorizes the caller against this workflow's project (the
   * node-graph id alone isn't authorizable pre-run). Sent as camelCase
   * `workflowId`; the DB reads it as `workflow_id`.
   *
   * Omitted ONLY for `surface: "lora_training"` items (power#916): LoRA
   * character training has no workflow, `subjectEntityId` is the LoRA id and
   * the backend authorizes via the LoRA read check instead.
   */
  workflowId?: string;
  /** Only workflow-node runs are estimable today. */
  subjectEntityType: "workflow_node_instance";
  /** The node-instance id whose run is being priced. */
  subjectEntityId: string;
  /** Numeric action id (resolved from the node type's `service.action.id`). */
  actionId: number;
  /** Origin surface for the estimate (defaults server-side to "workflow_node"). */
  surface?: string;
  /** Optional per-run parameters that may influence price (size, count, …). */
  params?: Record<string, unknown>;
}

/** One priced result item, keyed back to the request by `clientRef`. */
export interface ActionCostEstimate {
  clientRef: string;
  /** Estimated cost in Studio1 credits; null when uncovered. */
  estimatedCredits: number | null;
  /**
   * How the figure was derived:
   * - `"known"`  → deterministic per-unit price (e.g. image gen for 1 unit) —
   *   render the credit figure as-is.
   * - `"estimated"` → token/usage-based (text/LLM) — the figure is a best-effort
   *   UPPER BOUND (0 unless usage is supplied). Render it as a "≤" high estimate
   *   (`isCeilingQuote`). Seedance `video_tokens` quotes are the worst case the
   *   worker holds, so they get the marker too; the worker caps the charge at
   *   the hold (orchestrator-alpha#620).
   */
  costKind: "known" | "estimated";
  coverage: "over-quote" | "unknown";
  provider?: string | null;
  resolvedModel?: string | null;
}

interface EstimateActionCostsResponse {
  batch_id: string;
  items: Array<{
    client_ref: string;
    estimated_credits: number | null;
    cost_kind: "known" | "estimated";
    coverage: "over-quote" | "unknown";
    provider?: string | null;
    resolved_model?: string | null;
  }>;
}

/**
 * Estimate the cost of one or more billable actions against a subscription.
 * Charges route to whichever account is active in the header switcher, so the
 * caller passes that account's `subscriptionId`.
 */
export async function estimateActionCost(req: {
  subscriptionId: string;
  items: ActionCostEstimateItem[];
}): Promise<ActionCostEstimate[]> {
  const res = await apiClient.post<ApiEnvelope<EstimateActionCostsResponse>>({
    url: "/v1/billing/estimate",
    body: {
      subscriptionId: req.subscriptionId,
      items: req.items,
    } as unknown as undefined,
  });
  const data = unwrap(res, "Failed to estimate action cost");
  return data.items.map((item) => ({
    clientRef: item.client_ref,
    estimatedCredits: item.estimated_credits,
    costKind: item.cost_kind,
    coverage: item.coverage,
    provider: item.provider ?? null,
    resolvedModel: item.resolved_model ?? null,
  }));
}

// ============================================================================
// Per-execution charges (run-detail readout — GET
// /v1/billing/executions/:workflowExecutionId/charges)
//
// The durable, per-node "what did this run actually cost vs. what was quoted"
// readout (epic 1.7 "no surprise bills", power#1288 / frontend-alpha#642). One
// row per node in the execution, keyed by `subjectEntityId` — the node-instance
// id, the SAME key the pre-run estimate prices against (see
// `ActionCostEstimateItem.subjectEntityId`). This lets the run detail line up
// the actual charge with the earlier quote for the same node.
//
// The figure is the platform's own settled record (`_billing.quote_observations
// .actual_charge_amount`, stamped by `_credit.settle_hold()`), read via a
// user-authed product endpoint — NOT a client-side wallet delta. `settledAt` is
// null while the hold is still settling (a "pending" row); `actualCredits` is
// null until then. `quotedCostKind='estimated'` marks a token/usage-based quote
// that was an UPPER BOUND (render the "≤ / high est." marker, as the estimate
// does, via `isCeilingQuote`). A Seedance quote is its worst-case hold, and the
// worker caps the charge at it (orchestrator-alpha#620).
//
// NOTE: the backing endpoint is not live yet (its T2 api-alpha route + T1 DB fn
// were closed with the epic's test-only scope correction). The data layer is
// wired to the agreed contract so the UI lights up the moment a product
// endpoint lands — mirroring how `useActionCostEstimate` shipped ahead of its
// route. The hook keeps the query disabled until then (see
// `useExecutionCharges`).
// ============================================================================

export interface ExecutionCharge {
  /**
   * The node-instance id this charge belongs to — the join key to the node in
   * the execution's node list AND to the pre-run estimate. Equivalent to a node
   * execution's `workflowNodeId`.
   */
  subjectEntityId: string;
  /** Numeric action id that was run (node type's `service.action.id`); may be null. */
  actionId: number | null;
  /** Credits QUOTED for this node before the run; null when uncovered. */
  quotedCredits: number | null;
  /**
   * How the quote was derived — `"known"` (deterministic per-unit) or
   * `"estimated"` (token/usage-based UPPER BOUND, render with the "≤ / high est."
   * marker; a Seedance video-token quote is its worst-case hold).
   * Mirrors {@link ActionCostEstimate.costKind}.
   */
  quotedCostKind: "known" | "estimated";
  /**
   * Credits ACTUALLY charged once the hold settled; null while still pending
   * (see `settledAt`). This is the platform's settled figure, not a wallet delta.
   */
  actualCredits: number | null;
  /** ISO settlement timestamp; null means the charge is still PENDING. */
  settledAt: string | null;
  /**
   * Optional human/i18n-friendly reason the actual differed from the quote
   * (e.g. fewer tokens used than the upper bound). Null when not supplied.
   */
  varianceReason: string | null;
  provider?: string | null;
  resolvedModel?: string | null;
}

/**
 * Returns the per-node settled/pending charges for one workflow execution,
 * quoted vs. actual. Authorization is delegated to the backend (team-scoped);
 * a cross-tenant or non-existent execution resolves to a 404 (no existence
 * leak), never a 500. Pure read — no vendor spend, no wallet math.
 */
export async function getExecutionCharges(
  workflowExecutionId: string,
): Promise<ExecutionCharge[]> {
  const res = await apiClient.get<ApiEnvelope<ExecutionCharge[]>>({
    url: `/v1/billing/executions/${workflowExecutionId}/charges`,
  });
  return unwrap(res, "Failed to fetch execution charges");
}
